import type { SegmentRange } from '../../pages/comparison/types';

export interface LapRow {
  lap?: number;
  name?: string;
  total_elapsed_time?: number | null;
  total_timer_time?: number | null;
  total_distance?: number | null;
  start_time?: number | null;
  time_stamp?: number | null;
  avg_power?: number | null;
  average_power?: number | null;
  avg_heart_rate?: number | null;
  avg_speed?: number | null;
  avg_cadence?: number | null;
  tss?: number | null;
  tss_actual?: number | null;
}

const UNIX_TS_THRESHOLD = 1_000_000_000;

function lapField(lap: LapRow, ...keys: string[]): number | null {
  for (const key of keys) {
    const v = (lap as Record<string, unknown>)[key];
    if (v != null && v !== '') {
      const n = Number(v);
      if (isFinite(n)) return n;
    }
  }
  return null;
}

/** Lap duration in seconds — API/Go processor usually expose total_timer_time, not total_elapsed_time. */
export function lapDurationSec(lap: LapRow): number | null {
  return lapField(lap, 'total_elapsed_time', 'elapsed_time', 'total_timer_time');
}

export function lapDistanceM(lap: LapRow): number | null {
  const raw = lapField(lap, 'total_distance', 'distance');
  if (raw == null) return null;
  if (raw >= 1000) return raw;
  // Lap exports sometimes store km with decimals (e.g. 4.98); integer values are meters.
  if (!Number.isInteger(raw)) return Math.round(raw * 1000);
  return raw;
}

export function lapAvgHr(lap: LapRow): number | null {
  return lapField(lap, 'avg_heart_rate', 'average_heart_rate', 'heart_rate');
}

export function lapAvgSpeed(lap: LapRow): number | null {
  return lapField(lap, 'avg_speed', 'average_speed', 'speed');
}

export function lapAvgCadence(lap: LapRow): number | null {
  return lapField(lap, 'avg_cadence', 'average_cadence', 'cadence');
}

export function lapTss(lap: LapRow): number | null {
  return lapField(lap, 'tss_actual', 'tss');
}

/** First FIT unix start_time across laps — anchor for elapsed conversion. */
function workoutEpochSec(laps: LapRow[]): number | null {
  for (const lap of laps) {
    const start = lapField(lap, 'start_time');
    if (start != null && start >= UNIX_TS_THRESHOLD) return start;
  }
  return null;
}

/** Elapsed bounds from FIT unix start/end relative to workout epoch. */
function lapSegmentFromUnixTimestamps(
  lap: LapRow,
  epochSec: number,
  workoutDurationSec: number,
): SegmentRange | null {
  const startTs = lapField(lap, 'start_time');
  if (startTs == null || startTs < UNIX_TS_THRESHOLD) return null;

  let startSec = startTs - epochSec;
  let endSec: number | null = null;

  const endTs = lapField(lap, 'time_stamp');
  const dur = lapDurationSec(lap);
  let endSecFromTs: number | null = null;
  if (endTs != null && endTs >= UNIX_TS_THRESHOLD && endTs > startTs) {
    endSecFromTs = endTs - epochSec;
  }
  let endSecFromDur: number | null = null;
  if (dur != null && dur > 0) endSecFromDur = startSec + dur;

  // FIT lap time_stamp can end at the last record (too early) while total_timer_time is authoritative.
  if (endSecFromDur != null && endSecFromTs != null) {
    endSec = Math.max(endSecFromTs, endSecFromDur);
  } else {
    endSec = endSecFromDur ?? endSecFromTs;
  }

  if (endSec == null || endSec <= startSec) return null;
  if (startSec < 0) startSec = 0;

  return {
    startSec,
    endSec: Math.min(endSec, workoutDurationSec),
  };
}

/** Map a lap index to elapsed-time segment bounds using cumulative lap durations. */
export function lapIndexToSegmentRange(
  laps: LapRow[],
  lapIndex: number,
  workoutDurationSec: number,
): SegmentRange | null {
  const lap = laps[lapIndex];
  if (!lap) return null;

  let startSec = 0;
  let cumulativeOk = true;
  for (let i = 0; i < lapIndex; i++) {
    const dur = lapDurationSec(laps[i]);
    if (dur == null || dur <= 0) {
      cumulativeOk = false;
      break;
    }
    startSec += dur;
  }

  const lapDur = lapDurationSec(lap);
  if (cumulativeOk && lapDur != null && lapDur > 0) {
    return {
      startSec,
      endSec: Math.min(startSec + lapDur, workoutDurationSec),
    };
  }

  const epoch = workoutEpochSec(laps);
  if (epoch != null) {
    const fromUnix = lapSegmentFromUnixTimestamps(lap, epoch, workoutDurationSec);
    if (fromUnix) return fromUnix;
  }

  const startTs = lapField(lap, 'start_time');
  const endTs = lapField(lap, 'time_stamp');
  if (
    startTs != null &&
    endTs != null &&
    endTs > startTs &&
    startTs < UNIX_TS_THRESHOLD &&
    endTs < UNIX_TS_THRESHOLD
  ) {
    return {
      startSec: startTs,
      endSec: Math.min(endTs, workoutDurationSec),
    };
  }

  return null;
}

export function lapDisplayName(lap: LapRow, index: number): string {
  if (lap.name) return lap.name;
  const n = lap.lap != null ? Number(lap.lap) + 1 : index + 1;
  return `Lap ${n}`;
}

export function lapAvgPower(lap: LapRow): number | null {
  const v = lap.avg_power ?? lap.average_power;
  return v != null && isFinite(Number(v)) ? Number(v) : null;
}
